/**
 * Appearance axes — independent, freely-composable dimensions (see styles/themes.css).
 * Each axis maps to a data-* attribute on <html>; the user picks each one separately and
 * they combine in any permutation. Mode (light/dark) stays on body.theme-light (legacy).
 */
export interface AxisOption {
  id: string
  name: string
  /** two hexes for the accent swatch preview (accent axis only) */
  swatch?: [string, string]
}

export interface Axis {
  key: 'accent' | 'font' | 'radius' | 'density' | 'surface' | 'motion' | 'texture' | 'container' | 'navstyle' | 'icons'
  attr: string
  label: string
  options: AxisOption[]
  default: string
}

export const ACCENTS: AxisOption[] = [
  { id: 'default', name: 'Aurora', swatch: ['#06b6d4', '#8b5cf6'] },
  { id: 'verde', name: 'Verde', swatch: ['#10b981', '#14b8a6'] },
  { id: 'nocturne', name: 'Nocturne', swatch: ['#6366f1', '#8b5cf6'] },
  { id: 'rose', name: 'Rosé', swatch: ['#f43f5e', '#ec4899'] },
  { id: 'ember', name: 'Ember', swatch: ['#f59e0b', '#f97316'] },
  { id: 'marine', name: 'Marine', swatch: ['#3b82f6', '#0ea5e9'] },
  { id: 'nebula', name: 'Nebula', swatch: ['#8b5cf6', '#d946ef'] },
  { id: 'lagoon', name: 'Lagoon', swatch: ['#14b8a6', '#06b6d4'] },
  { id: 'scarlet', name: 'Scarlet', swatch: ['#ef4444', '#fb7185'] },
  { id: 'citrus', name: 'Citrus', swatch: ['#84cc16', '#10b981'] },
  { id: 'terracotta', name: 'Terracotta', swatch: ['#c2410c', '#d97706'] },
  { id: 'phosphor', name: 'Phosphor', swatch: ['#22c55e', '#f59e0b'] },
  { id: 'live', name: 'Live', swatch: ['#25f4ee', '#fe2c55'] },
  { id: 'royal', name: 'Royal', swatch: ['#4f46e5', '#3b82f6'] },
  { id: 'sunset', name: 'Sunset', swatch: ['#f97316', '#ef4444'] },
  { id: 'orchid', name: 'Orchid', swatch: ['#c084fc', '#e879f9'] },
  { id: 'forest', name: 'Forest', swatch: ['#16a34a', '#65a30d'] },
  { id: 'aurum', name: 'Aurum', swatch: ['#eab308', '#f59e0b'] },
  { id: 'steel', name: 'Steel', swatch: ['#64748b', '#0ea5e9'] },
  { id: 'mint', name: 'Mint', swatch: ['#2dd4bf', '#34d399'] },
  { id: 'crimson', name: 'Crimson', swatch: ['#dc2626', '#e11d48'] },
  { id: 'abyss', name: 'Abyss', swatch: ['#1d4ed8', '#4338ca'] },
  { id: 'flamingo', name: 'Flamingo', swatch: ['#fb7185', '#fb923c'] },
  { id: 'mocha', name: 'Mocha', swatch: ['#b45309', '#a16207'] },
]

export const FONTS: AxisOption[] = [
  { id: 'sans', name: 'Sans · Inter' },
  { id: 'serif', name: 'Serif · Fraunces' },
  { id: 'mono', name: 'Mono · JetBrains' },
  { id: 'grotesk', name: 'Grotesk · Space Grotesk' },
  { id: 'jakarta', name: 'Jakarta · Plus Jakarta Sans' },
  { id: 'outfit', name: 'Geo · Outfit' },
  { id: 'manrope', name: 'Modern · Manrope' },
  { id: 'lora', name: 'Klasik · Lora' },
]

export const RADII: AxisOption[] = [
  { id: 'sharp', name: 'Keskin' },
  { id: 'soft', name: 'Yumuşak' },
  { id: 'default', name: 'Normal' },
  { id: 'rounded', name: 'Yuvarlak' },
  { id: 'full', name: 'Full' },
]

export const DENSITIES: AxisOption[] = [
  { id: 'compact', name: 'Sıkı' },
  { id: 'default', name: 'Normal' },
  { id: 'comfortable', name: 'Ferah' },
]

export const SURFACES: AxisOption[] = [
  { id: 'glass', name: 'Cam' },
  { id: 'soft', name: 'Yumuşak' },
  { id: 'flat', name: 'Düz' },
]

export const MOTIONS: AxisOption[] = [
  { id: 'full', name: 'Tam' },
  { id: 'reduced', name: 'Azalt' },
  { id: 'none', name: 'Kapalı' },
]

export const TEXTURES: AxisOption[] = [
  { id: 'none', name: 'Yok' },
  { id: 'dots', name: 'Nokta' },
]

export const CONTAINERS: AxisOption[] = [
  { id: 'full', name: 'Tam' },
  { id: 'contained', name: 'Sınırlı' },
]

export const NAVSTYLES: AxisOption[] = [
  { id: 'classic', name: 'Klasik' },
  { id: 'topnav', name: 'Üst menü' },
  { id: 'floating', name: 'Yüzen' },
  { id: 'minimal', name: 'Minimal' },
]

export const ICONSETS: AxisOption[] = [
  { id: 'line', name: 'Çizgi' },
  { id: 'solid', name: 'Dolu' },
  { id: 'geo', name: 'Geometrik' },
]

export const AXES: Axis[] = [
  { key: 'accent', attr: 'data-accent', label: 'Renk', options: ACCENTS, default: 'default' },
  { key: 'font', attr: 'data-font', label: 'Yazı tipi', options: FONTS, default: 'sans' },
  { key: 'radius', attr: 'data-radius', label: 'Köşe', options: RADII, default: 'default' },
  { key: 'density', attr: 'data-density', label: 'Yoğunluk', options: DENSITIES, default: 'default' },
  { key: 'surface', attr: 'data-surface', label: 'Yüzey', options: SURFACES, default: 'glass' },
  { key: 'motion', attr: 'data-motion', label: 'Animasyon', options: MOTIONS, default: 'full' },
  { key: 'texture', attr: 'data-texture', label: 'Doku', options: TEXTURES, default: 'none' },
  { key: 'container', attr: 'data-container', label: 'Genişlik', options: CONTAINERS, default: 'full' },
  { key: 'navstyle', attr: 'data-nav', label: 'Menü stili', options: NAVSTYLES, default: 'classic' },
  { key: 'icons', attr: 'data-icons', label: 'İkon seti', options: ICONSETS, default: 'line' },
]

export type AppearanceKey = Axis['key']

export function isValidAxisValue(axis: Axis, id: string | null | undefined): id is string {
  return !!id && axis.options.some((o) => o.id === id)
}
